<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="payments"
        :row-key="'payment_id'"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1280 }"
        class="text-nowrap align-middle sales-payment-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ $t(`${i18nScope}.no_sales_payment_reports_found`) }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'payment_method'">
            {{ record.payment_method || $t(`${i18nScope}.not_available`) }}
          </template>

          <template v-else-if="column && column.key === 'paid_by'">
            {{ record.paid_by || $t(`${i18nScope}.not_available`) }}
          </template>

          <template v-else-if="column && column.key === 'amount'">
            ${{ formatAmount(record.amount) }}
          </template>

          <template v-else-if="column && column.key === 'note'">
            {{ record.note || $t(`${i18nScope}.not_available_short`) }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";
import type { SalesPaymentReportItem } from "@/services/report";

export default defineComponent({
  name: "SalesPaymentReport",
  props: {
    payments: {
      type: Array as () => SalesPaymentReportItem[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    i18nScope: {
      type: String,
      default: "sales_payment_report_page",
    },
  },
  emits: ["table-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const columns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_payment_ref`), key: "payment_ref", dataIndex: "payment_ref", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_sale_ref`), key: "sale_ref", dataIndex: "sale_ref", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_customer`), key: "customer_name", dataIndex: "customer_name", width: 220, align: "center" as const },
      {
        title: t(`${props.i18nScope}.table_payment_method`),
        key: "payment_method",
        dataIndex: "payment_method",
        width: 170,
        align: "center" as const,
      },
      { title: t(`${props.i18nScope}.table_paid_by`), key: "paid_by", dataIndex: "paid_by", width: 170, align: "center" as const },
      { title: t(`${props.i18nScope}.table_amount`), key: "amount", dataIndex: "amount", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_note`), key: "note", dataIndex: "note", width: 220, align: "center" as const },
    ]);

    const formatDate = (dateString?: string) => {
      if (!dateString) return t(`${props.i18nScope}.not_available`);
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatAmount = (value?: string | number) => Number(value ?? 0).toFixed(2);

    const handleChange = (pagination: TablePaginationConfig) => {
      emit("table-change", pagination.current || 1, pagination.pageSize || 10);
    };

    return {
      columns,
      formatDate,
      formatAmount,
      handleChange,
    };
  },
});
</script>

<style scoped>
.sales-payment-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.sales-payment-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}
</style>
